import { useEffect, useRef, useState } from "react";

type Mensagem = {
  id: string;
  autor: "user" | "bot";
  texto: string;
  hora: string;
};

export default function ChatSuportePage() {
  const [mensagem, setMensagem] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessaoId, setSessaoId] = useState<string>(
    localStorage.getItem("chat_suporte_sessao_id") || ""
  );

  const authUser = window.__AUTH_USER__;
  const role = authUser?.role;

  const mensagemInicial =
    role === "anfitriao"
      ? "Olá! Posso ajudar com dúvidas sobre serviços, agenda, reservas, pagamentos e configurações da plataforma AllePets."
      : "Olá! Posso ajudar com reservas, pets, serviços, pagamentos e dúvidas da plataforma AllePets.";

  const subtitulo =
    role === "anfitriao"
      ? "Tire suas dúvidas sobre serviços, agenda, reservas e pagamentos sem sair do painel."
      : "Tire suas dúvidas sobre reservas, pets, pagamentos e serviços sem sair do painel.";

  const [perguntasRapidas, setPerguntasRapidas] = useState<string[]>([
    "Como faço uma reserva?",
    "Como cancelar uma reserva?",
    "Como cadastrar meu pet?",
    "Onde vejo meus pagamentos?",
  ]);

  const horaAgora = () =>
    new Date().toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });

  const [mensagens, setMensagens] = useState<Mensagem[]>([
    {
      id: "init",
      autor: "bot",
      texto: mensagemInicial,
      hora: horaAgora(),
    },
  ]);

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [mensagens, loading]);

  // Auto-resize do textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
  }, [mensagem]);

  async function enviarMensagem(textoOpcional?: string) {
    const texto = (textoOpcional ?? mensagem).trim();

    if (!texto || loading) return;

    const novaMensagemUsuario: Mensagem = {
      id: crypto.randomUUID(),
      autor: "user",
      texto,
      hora: horaAgora(),
    };

    setMensagens((prev) => [...prev, novaMensagemUsuario]);
    setMensagem("");
    setLoading(true);

    try {
      const csrf =
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content") || "";

      const response = await fetch("/chat-suporte", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-TOKEN": csrf,
        },
        credentials: "same-origin",
        body: JSON.stringify({
          mensagem: texto,
          sessao_id: sessaoId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setMensagens((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            autor: "bot",
            texto:
              data?.resposta ||
              data?.message ||
              "Erro ao processar sua solicitação.",
            hora: horaAgora(),
          },
        ]);
        return;
      }

      if (data?.sessao_id) {
        setSessaoId(data.sessao_id);
        localStorage.setItem("chat_suporte_sessao_id", data.sessao_id);
      }

      if (Array.isArray(data?.perguntas_rapidas)) {
        setPerguntasRapidas(data.perguntas_rapidas);
      }

      setMensagens((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          autor: "bot",
          texto: data?.resposta || "Não consegui responder no momento.",
          hora: horaAgora(),
        },
      ]);
    } catch (error) {
      console.error(error);

      setMensagens((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          autor: "bot",
          texto: "Erro de conexão ao falar com o suporte.",
          hora: horaAgora(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function limparConversa() {
    localStorage.removeItem("chat_suporte_sessao_id");
    setSessaoId("");
    setMensagens([
      {
        id: "reset",
        autor: "bot",
        texto: mensagemInicial,
        hora: horaAgora(),
      },
    ]);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50/40">
      <div className="mx-auto w-full max-w-7xl px-3 py-4 sm:px-6 sm:py-8 lg:px-8">
        {/* Cabeçalho */}
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center gap-2 text-xs font-medium text-sky-600 sm:text-sm">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-500" />
            Assistente IA • AllePets
          </div>
          <h1 className="mt-1.5 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Tire suas dúvidas
          </h1>
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-slate-500 sm:text-base">
            {authUser?.name ? `Olá, ${authUser.name}. ` : ""}
            {subtitulo}
          </p>
        </div>

        {/* Layout principal: chat + sidebar (desktop) */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px] lg:gap-6">
          {/* Coluna do chat */}
          <div className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm sm:rounded-3xl">
            {/* Header do chat */}
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-white/80 px-4 py-3.5 backdrop-blur sm:px-6 sm:py-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-sky-600 text-white shadow-sm sm:h-11 sm:w-11">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5 sm:h-[22px] sm:w-[22px]"
                  >
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                  </svg>
                  <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-500" />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-slate-900 sm:text-base">
                    Assistente AllePets
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-emerald-600 sm:text-sm">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                    </span>
                    Online agora
                  </div>
                </div>
              </div>

              {/* Botão limpar (só mobile; no desktop fica na sidebar) */}
              <button
                type="button"
                onClick={limparConversa}
                disabled={loading}
                className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50 lg:hidden"
                aria-label="Limpar conversa"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-3.5 w-3.5"
                >
                  <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                </svg>
                Limpar
              </button>
            </div>

            {/* Área de mensagens */}
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-slate-50/60 to-slate-50 px-3 py-4 sm:px-6 sm:py-6 min-h-[50vh] max-h-[calc(100vh-380px)] lg:min-h-[500px] lg:max-h-[600px]">
              <div className="space-y-4 sm:space-y-5">
                {mensagens.map((item) => {
                  const isUser = item.autor === "user";
                  return (
                    <div
                      key={item.id}
                      className={`flex items-end gap-2 sm:gap-3 ${
                        isUser ? "justify-end" : "justify-start"
                      }`}
                    >
                      {/* Avatar do bot */}
                      {!isUser && (
                        <div className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-sky-600 text-white shadow-sm sm:flex">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-4 w-4"
                          >
                            <circle cx="12" cy="12" r="3" />
                            <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                          </svg>
                        </div>
                      )}

                      <div
                        className={`flex max-w-[85%] flex-col sm:max-w-[75%] lg:max-w-[68%] ${
                          isUser ? "items-end" : "items-start"
                        }`}
                      >
                        <div
                          className={
                            isUser
                              ? "rounded-2xl rounded-br-sm bg-gradient-to-br from-sky-600 to-sky-700 px-4 py-2.5 text-sm leading-relaxed text-white shadow-sm sm:px-5 sm:py-3 sm:text-[15px]"
                              : "rounded-2xl rounded-bl-sm border border-slate-200 bg-white px-4 py-2.5 text-sm leading-relaxed text-slate-800 shadow-sm sm:px-5 sm:py-3 sm:text-[15px]"
                          }
                        >
                          <div className="whitespace-pre-wrap break-words">
                            {item.texto}
                          </div>
                        </div>
                        <div
                          className={`mt-1 px-1 text-[11px] text-slate-400 sm:text-xs ${
                            isUser ? "text-right" : "text-left"
                          }`}
                        >
                          {isUser ? "Você" : "AllePets"} • {item.hora}
                        </div>
                      </div>

                      {/* Avatar do usuário */}
                      {isUser && (
                        <div className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600 shadow-sm sm:flex">
                          {authUser?.name
                            ? authUser.name.charAt(0).toUpperCase()
                            : "V"}
                        </div>
                      )}
                    </div>
                  );
                })}

                {loading && (
                  <div className="flex items-end gap-2 sm:gap-3">
                    <div className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-sky-600 text-white shadow-sm sm:flex">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4"
                      >
                        <circle cx="12" cy="12" r="3" />
                        <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                      </svg>
                    </div>
                    <div className="rounded-2xl rounded-bl-sm border border-slate-200 bg-white px-4 py-3 shadow-sm">
                      <div className="flex items-center gap-1">
                        <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.3s]" />
                        <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.15s]" />
                        <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400" />
                      </div>
                    </div>
                  </div>
                )}

                <div ref={bottomRef} />
              </div>
            </div>

            {/* Perguntas rápidas (scroll horizontal no mobile) */}
            <div className="border-t border-slate-100 bg-white px-3 pt-3 sm:px-6 sm:pt-4">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-slate-500 sm:text-sm">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-3.5 w-3.5"
                >
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
                Perguntas sugeridas
              </div>
              <div className="-mx-3 flex gap-2 overflow-x-auto px-3 pb-2 sm:-mx-6 sm:flex-wrap sm:overflow-visible sm:px-6 sm:pb-0 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                {perguntasRapidas.map((pergunta) => (
                  <button
                    key={pergunta}
                    type="button"
                    onClick={() => enviarMensagem(pergunta)}
                    disabled={loading}
                    className="shrink-0 whitespace-nowrap rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs font-medium text-slate-700 transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700 active:scale-95 disabled:opacity-50 sm:text-sm"
                  >
                    {pergunta}
                  </button>
                ))}
              </div>
            </div>

            {/* Input */}
            <div className="border-t border-slate-100 bg-white px-3 py-3 sm:px-6 sm:py-4">
              <div className="flex items-end gap-2">
                <div className="flex-1 rounded-2xl border border-slate-200 bg-slate-50 transition focus-within:border-sky-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-sky-100">
                  <textarea
                    ref={textareaRef}
                    value={mensagem}
                    onChange={(e) => setMensagem(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        enviarMensagem();
                      }
                    }}
                    rows={1}
                    placeholder="Digite sua dúvida..."
                    className="block max-h-40 min-h-[44px] w-full resize-none bg-transparent px-4 py-3 text-sm text-slate-800 outline-none placeholder:text-slate-400 sm:text-[15px]"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => enviarMensagem()}
                  disabled={loading || !mensagem.trim()}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-600 to-sky-700 text-white shadow-sm transition hover:shadow-md active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 sm:h-12 sm:w-12"
                  aria-label="Enviar mensagem"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5"
                  >
                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                  </svg>
                </button>
              </div>

              <div className="mt-2 hidden items-center gap-1.5 text-[11px] text-slate-400 sm:flex">
                <kbd className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px]">
                  Enter
                </kbd>
                enviar •
                <kbd className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px]">
                  Shift + Enter
                </kbd>
                nova linha
              </div>
            </div>
          </div>

          {/* Sidebar (apenas desktop) */}
          <aside className="hidden flex-col gap-4 lg:flex">
            {/* Card de boas-vindas */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-sky-500 to-sky-700 p-5 text-white shadow-sm">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div className="text-base font-semibold">Resposta na hora</div>
              <div className="mt-1 text-sm leading-relaxed text-sky-50/90">
                Nossa IA responde 24/7 sobre reservas, pets, pagamentos e
                configurações da plataforma.
              </div>
            </div>

            {/* Dicas */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-3 text-sm font-semibold text-slate-900">
                Dicas para melhores respostas
              </div>
              <ul className="space-y-2.5 text-sm text-slate-600">
                <li className="flex gap-2">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-50 text-xs font-semibold text-sky-600">
                    1
                  </span>
                  <span>Seja específico sobre o que precisa resolver.</span>
                </li>
                <li className="flex gap-2">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-50 text-xs font-semibold text-sky-600">
                    2
                  </span>
                  <span>Inclua detalhes como datas ou nome do serviço.</span>
                </li>
                <li className="flex gap-2">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-50 text-xs font-semibold text-sky-600">
                    3
                  </span>
                  <span>Use as perguntas sugeridas como ponto de partida.</span>
                </li>
              </ul>
            </div>

            {/* Ações */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-3 text-sm font-semibold text-slate-900">
                Conversa
              </div>
              <button
                type="button"
                onClick={limparConversa}
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                >
                  <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                </svg>
                Limpar conversa
              </button>
              <div className="mt-3 text-xs text-slate-400">
                Isso apagará o histórico desta sessão.
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
